import React, { useState, useEffect, Fragment } from 'react';
import PropTypes, { object } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useContent } from 'contexts/content-context';

import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import Account from '../Account/Account';
import {
  Table,
  Location,
  Button,
  Icon,
  TablePerson,
  TableCell
} from 'components/common/Elements/Elements';
import AdditionalContent from '../AdditionalContent/AdditionalContent';
import ActualStatusMarker from '../../../../Customer/Platform/pages/PlatformNetworks/parts/ActualStatusMarker/ActualStatusMarker';

import { getBeaconStatus } from 'utils/common';

import { lastVersionNetwork } from 'utils/settings';

import classes from './NetworksTable.module.scss';
import { access, hasAccess, resources } from 'utils/permissions';

const TableRow = ({
  companyInfo,
  tableConf,
  row,
  mouseEnter,
  idOpenTunnels,
  handleOpenTunnels,
  onOpenAdditionalModal
}) => {
  const { contentState } = useContent();
  const { t } = useTranslation(['activity']);

  const [company, setCompany] = useState(companyInfo);
  const hasTenantSensitiveDataReadAccess = hasAccess([
    resources.TENANT_SENSITIVE_DATA,
    access.READ
  ]);

  useEffect(() => {
    async function getBeaconData() {
      let cancelled = false;

      if (companyInfo.isBeaconStatusExist) {
        const beaconStatus = await getBeaconStatus(
          companyInfo.networkId,
          {
            customerId: companyInfo.customerId,
            networkId: companyInfo.networkId
          },
          cancelled
        );
        if (!isEmpty(beaconStatus))
          if (beaconStatus.networkBeaconProgress == 100)
            setCompany({
              ...companyInfo,
              isBeaconStatusExist: false,
              networkVersion: lastVersionNetwork
            });
          else if (typeof beaconStatus.networkBeaconProgress != 'undefined')
            setCompany({
              ...companyInfo,
              beaconProgress: beaconStatus.networkBeaconProgress
            });
      }
    }
    getBeaconData();
    async function getActualStatus() {
      const componentStatus = contentState.actualStatus.find(
        itemStatus => itemStatus.id == company.gatewayId
      );

      if (componentStatus) {
        const newCompany = {
          ...company,
          actualStatus: componentStatus.status
        };

        setCompany(newCompany);
      }
    }
    getActualStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo, contentState]);

  return (
    <>
      <Table.Row className={classes.row}>
        {tableConf.map(({ show, field }, ind) => {
          const cellProps = {
            row: row,
            mouseEnter: mouseEnter,
            ind: ind,
            field: field,
            classes: classes
          };

          return (
            <Fragment key={`${field}-${company.email}}`}>
              {show && field === 'companyName' && (
                <TableCell cellProps={cellProps}>
                  {company.tunnels.length > 0 && (
                    <Button
                      className={cn(
                        classes.button,
                        classes.button_wrap,
                        idOpenTunnels === company.gatewayId &&
                          classes.button_open
                      )}
                      onClick={() => {
                        handleOpenTunnels(company.gatewayId);
                      }}
                      aria-label="Open/close the Tunnels div"
                    >
                      <Icon
                        size="16px"
                        icon="arrow"
                        className={
                          idOpenTunnels !== company.gatewayId
                            ? classes.iconDown
                            : classes.iconUp
                        }
                      />
                      {idOpenTunnels === company.gatewayId && (
                        <Icon
                          icon="parentLine"
                          size="73px"
                          className={cn(classes.lineDot)}
                        />
                      )}
                    </Button>
                  )}
                  {company.tunnels.length < 1 && (
                    <div className={classes.nullButton} />
                  )}
                  <Account data={company} />
                </TableCell>
              )}
              {show && field === 'networkName' && (
                <TableCell cellProps={cellProps}>
                  {company.networkName || 'NA'}
                </TableCell>
              )}
              {show && field === 'networkId' && (
                <TableCell isId cellProps={cellProps}>
                  {company.networkId ? (
                    hasTenantSensitiveDataReadAccess ? (
                      <Button
                        className={classes.btnDetails}
                        onClick={() =>
                          onOpenAdditionalModal({
                            content: (
                              <AdditionalContent id={company.networkId} />
                            )
                          })
                        }
                      >
                        {company.networkId}
                      </Button>
                    ) : (
                      company.networkId
                    )
                  ) : (
                    'NA'
                  )}
                </TableCell>
              )}
              {show && field === 'gatewayImageType' && (
                <TableCell cellProps={cellProps}>
                  {company.gatewayImageType || 'NA'}
                </TableCell>
              )}
              {show && field === 'gatewayImageVersion' && (
                <TableCell cellProps={cellProps}>
                  {company.gatewayImageVersion || 'NA'}
                </TableCell>
              )}
              {show && field === 'networkDns' && (
                <TableCell cellProps={cellProps}>
                  {company.networkDns || 'NA'}
                </TableCell>
              )}
              {show && field === 'networkVersion' && (
                <TableCell cellProps={cellProps}>
                  {company.networkVersion || 'NA'}
                </TableCell>
              )}
              {show && field === 'provider' && (
                <TableCell cellProps={cellProps}>
                  {company.provider || 'NA'}
                </TableCell>
              )}
              {show && field === 'regionName' && (
                <TableCell cellProps={cellProps}>
                  {company.regionName || 'NA'}
                </TableCell>
              )}
              {show && field === 'gatewayId' && (
                <TableCell isId cellProps={cellProps}>
                  {company.gatewayId || 'NA'}
                </TableCell>
              )}
              {show && field === 'gatewayIp' && (
                <TableCell isId cellProps={cellProps}>
                  {company.gatewayIp || 'NA'}
                </TableCell>
              )}
              {show && field === 'gatewayStatus' && (
                <TableCell
                  cellProps={cellProps}
                  classNameContent={classes.actualStatuses}
                >
                  <ActualStatusMarker
                    customerId={company.customerId}
                    networkComponent={company}
                    componentType={'network'}
                    showStatusText={true}
                  />
                </TableCell>
              )}
              {show && field === 'regionCountry' && (
                <TableCell cellProps={cellProps}>
                  {company.regionCountry &&
                  company.regionCountry !== '-' &&
                  company.regionCountry !== 'NA' ? (
                    <Location
                      country={company.regionCountry}
                      type="c"
                      withTooltip
                    />
                  ) : (
                    'NA'
                  )}
                </TableCell>
              )}
              {show && field === 'providerRegionName' && (
                <TableCell cellProps={cellProps}>
                  {company.providerRegionName || 'NA'}
                </TableCell>
              )}

              {show && field === 'plan' && (
                <TableCell cellProps={cellProps}>
                  {company.plan && company.plan !== '-' && company.plan !== 'NA'
                    ? company.plan
                    : 'NA'}
                </TableCell>
              )}
              {show && field === 'billingCycle' && (
                <TableCell cellProps={cellProps}>
                  {company.billingCycle &&
                  company.billingCycle !== '-' &&
                  company.billingCycle !== 'NA'
                    ? company.billingCycle
                    : 'NA'}
                </TableCell>
              )}
              {show && field === 'accountManager' && (
                <TableCell
                  cellProps={cellProps}
                  classNameContent={classes.center}
                >
                  {company.accountManager &&
                  company.accountManager !==
                    t('activity:customers:UNASSIGNED') ? (
                    <TablePerson person={company.accountManager} />
                  ) : (
                    t('activity:customers:UNASSIGNED')
                  )}
                </TableCell>
              )}
              {show && field === 'customerSuccessEngineer' && (
                <TableCell
                  cellProps={cellProps}
                  classNameContent={classes.center}
                >
                  {company.customerSuccessEngineer &&
                  company.customerSuccessEngineer !==
                    t('activity:customers:UNASSIGNED') ? (
                    <TablePerson person={company.customerSuccessEngineer} />
                  ) : (
                    t('activity:customers:UNASSIGNED')
                  )}
                </TableCell>
              )}
              {show && field === 'regionStatus' && (
                <TableCell cellProps={cellProps}>
                  {company.regionStatus || 'NA'}
                </TableCell>
              )}
              {show && field === 'regionPublicIp' && (
                <TableCell cellProps={cellProps}>
                  {company.regionPublicIp.join(', ') || 'NA'}
                </TableCell>
              )}
            </Fragment>
          );
        })}
      </Table.Row>
    </>
  );
};

TableRow.propTypes = {
  companyInfo: PropTypes.object,
  tableConf: PropTypes.arrayOf(object),
  row: PropTypes.string,
  mouseEnter: PropTypes.func,
  handleCheckCustomer: PropTypes.func,
  isSelectedCustomer: PropTypes.func,
  CheckBox: PropTypes.func,
  handleOpenTunnels: PropTypes.func.isRequired,
  idOpenTunnels: PropTypes.string,
  onOpenAdditionalModal: PropTypes.func.isRequired
};

export default TableRow;
